// Premium Print: a thin, matte paper sheet with the original, unmodified artwork.
async function initializePremium(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const stage = root.querySelector('.pp-stage');
  const status = root.querySelector('.pp-status');
  const announcement = root.querySelector('.pp-announcement');
  const shadow = root.querySelector('.pp-shadow');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = matchMedia('(pointer: coarse)');
  const life = new AbortController();
  // Retain the shared tilt/size, with a separate, visibly flexible paper surface.
  const motionScale = 1.30;
  const configuration = { tilt: 3.84 * motionScale, inertia: .23, motion: true };
  const geometries = [], materials = [], textures = [];
  let disposed = false, renderer, resizeObserver, visibilityObserver;
  let frame = 0, lastTime = 0, visible = true, pointerInside = false;
  let playedMobile = false, demoStart = null, hasFocus = false;
  let px = 0, py = 0, rx = 0, ry = 0, lift = 0;
  const restingPaper = { bow: .026, curl: .006, twist: -.004 };
  let bow = restingPaper.bow, curl = restingPaper.curl, twist = restingPaper.twist;
  const loadTimer = setTimeout(() => {
    if (root.dataset.ready !== 'true') status.textContent = 'Die 3D-Ansicht wird noch geladen. Das Poster ist bereits sichtbar.';
  }, 9000);

  function dispose() {
    if (disposed) return;
    disposed = true;
    clearTimeout(loadTimer);
    if (frame) cancelAnimationFrame(frame);
    life.abort();
    resizeObserver?.disconnect();
    visibilityObserver?.disconnect();
    geometries.forEach(item => item.dispose());
    materials.forEach(item => item.dispose());
    textures.forEach(item => item.dispose());
    renderer?.dispose();
    renderer?.domElement.remove();
    delete root.dataset.initialized;
    delete root.dataset.ready;
  }
  root.disposePreview = dispose;

  try {
    const THREE = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js');
    if (disposed || !root.isConnected) { dispose(); return; }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = root.querySelector('.pp-paper-image').src;
    await image.decode();
    if (disposed || !root.isConnected) { dispose(); return; }
    const artwork = new THREE.Texture(image);
    artwork.colorSpace = THREE.SRGBColorSpace;
    artwork.minFilter = THREE.LinearMipmapLinearFilter;
    artwork.magFilter = THREE.LinearFilter;
    artwork.generateMipmaps = true;
    artwork.needsUpdate = true;
    textures.push(artwork);

    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.domElement.setAttribute('aria-hidden', 'true');
    stage.append(renderer.domElement);
    artwork.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());

    const scene = new THREE.Scene();
    const fov = 2 * Math.atan(36 / (2 * 105)) * 180 / Math.PI;
    const camera = new THREE.PerspectiveCamera(fov, 1, .01, 20);
    const distance = 2.1, yaw = THREE.MathUtils.degToRad(5.5), elevation = THREE.MathUtils.degToRad(2);
    camera.position.set(distance * Math.sin(yaw) * Math.cos(elevation), -.008 + distance * Math.sin(elevation), distance * Math.cos(yaw) * Math.cos(elevation));
    camera.lookAt(0, -.008, 0);
    const model = new THREE.Group();
    model.name = 'Premium Print · matte museum paper';
    scene.add(model);

    // Same outer height as Collectors; preserve the portrait's full aspect ratio.
    // A visible natural bow and a paper-thin edge, not a rigid board or a frame.
    const height = .52, width = height * image.naturalWidth / image.naturalHeight;
    const thickness = .00035;
    const paperZ = (x, y) => {
      const u = x / (width / 2), v = y / (height / 2);
      return bow * (u * u - .38) + curl * (v * v - .35) + twist * u * v;
    };
    function paperSurface(back = false) {
      const geometry = new THREE.PlaneGeometry(width, height, 48, 64);
      const positions = geometry.attributes.position;
      positions.setUsage(THREE.DynamicDrawUsage);
      for (let i = 0; i < positions.count; i++) {
        positions.setZ(i, paperZ(positions.getX(i), positions.getY(i)) + (back ? -thickness / 2 : thickness / 2));
      }
      geometry.computeVertexNormals();
      geometries.push(geometry);
      return geometry;
    }

    // Microscopic paper relief affects lighting only, never the artwork pixels.
    const grainCanvas = document.createElement('canvas');
    grainCanvas.width = grainCanvas.height = 512;
    const context = grainCanvas.getContext('2d');
    const grain = context.createImageData(512, 512);
    let seed = 250;
    for (let i = 0; i < grain.data.length; i += 4) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const shade = 108 + (seed >>> 26);
      grain.data[i] = grain.data[i + 1] = grain.data[i + 2] = shade;
      grain.data[i + 3] = 255;
    }
    context.putImageData(grain, 0, 0);
    const grainTexture = new THREE.CanvasTexture(grainCanvas);
    grainTexture.wrapS = grainTexture.wrapT = THREE.RepeatWrapping;
    grainTexture.repeat.set(4, 5);
    textures.push(grainTexture);
    const print = new THREE.MeshPhysicalMaterial({
      map: artwork, color: 0xffffff, metalness: 0, roughness: .98,
      specularIntensity: .08, clearcoat: 0, transmission: 0,
      bumpMap: grainTexture, bumpScale: .000003,
      side: THREE.FrontSide
    });
    const backing = new THREE.MeshStandardMaterial({ color: 0xf5f1e7, metalness: 0, roughness: 1, side: THREE.BackSide });
    const edge = new THREE.MeshStandardMaterial({ color: 0xe9e5d9, metalness: 0, roughness: 1, side: THREE.DoubleSide });
    materials.push(print, backing, edge);
    const frontGeometry = paperSurface(), backGeometry = paperSurface(true);
    model.add(new THREE.Mesh(frontGeometry, print));
    model.add(new THREE.Mesh(backGeometry, backing));

    const corners = [[-width / 2, -height / 2], [width / 2, -height / 2], [width / 2, height / 2], [-width / 2, height / 2]];
    const edgeVertices = [];
    for (let side = 0; side < 4; side++) {
      const a = corners[side], b = corners[(side + 1) % 4];
      const segments = side % 2 ? 64 : 48;
      for (let i = 0; i < segments; i++) {
        const x1 = a[0] + (b[0] - a[0]) * i / segments;
        const y1 = a[1] + (b[1] - a[1]) * i / segments;
        const x2 = a[0] + (b[0] - a[0]) * (i + 1) / segments;
        const y2 = a[1] + (b[1] - a[1]) * (i + 1) / segments;
        const z1 = paperZ(x1, y1), z2 = paperZ(x2, y2), half = thickness / 2;
        edgeVertices.push(x1, y1, z1 + half, x1, y1, z1 - half, x2, y2, z2 + half,
          x2, y2, z2 + half, x1, y1, z1 - half, x2, y2, z2 - half);
      }
    }
    const edgeGeometry = new THREE.BufferGeometry();
    edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgeVertices, 3).setUsage(THREE.DynamicDrawUsage));
    edgeGeometry.computeVertexNormals();
    geometries.push(edgeGeometry);
    model.add(new THREE.Mesh(edgeGeometry, edge));

    // Update both printed faces and their connecting rim together, without seams
    // or re-sampling the original image. Buffers are reused throughout the motion.
    const edgeOffsets = new Float32Array(edgeGeometry.attributes.position.count);
    for (let i = 0; i < edgeOffsets.length; i++) {
      const positions = edgeGeometry.attributes.position;
      edgeOffsets[i] = positions.getZ(i) - paperZ(positions.getX(i), positions.getY(i));
    }
    function updatePaper() {
      for (const [geometry, offset] of [[frontGeometry, thickness / 2], [backGeometry, -thickness / 2], [edgeGeometry, 0]]) {
        const positions = geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
          positions.setZ(i, paperZ(positions.getX(i), positions.getY(i)) + (geometry === edgeGeometry ? edgeOffsets[i] : offset));
        }
        positions.needsUpdate = true;
        geometry.computeVertexNormals();
        geometry.computeBoundingSphere();
      }
      root.dataset.curvature = `${(bow * 1000).toFixed(1)},${(curl * 1000).toFixed(1)},${(twist * 1000).toFixed(1)}`;
    }
    updatePaper();

    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.05); key.position.set(-.75, 1.05, 1.15); scene.add(key);
    const fill = new THREE.DirectionalLight(0xfffbf5, .55); fill.position.set(.85, .45, .85); scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, .8); rim.position.set(.35, 1, -.1); scene.add(rim);

    const requestFrame = () => { if (!disposed && visible && !document.hidden && !frame) frame = requestAnimationFrame(tick); };
    const motionAllowed = () => configuration.motion && !reduced.matches;
    function controlsChanged() {
      if (!motionAllowed()) {
        px = py = rx = ry = lift = 0; demoStart = null; pointerInside = false;
        ({ bow, curl, twist } = restingPaper); updatePaper();
      }
      requestFrame();
    }
    function resize() {
      if (disposed) return;
      const w = Math.ceil(renderer.domElement.getBoundingClientRect().width);
      if (w < 1) return;
      renderer.setPixelRatio(Math.min(Math.max(devicePixelRatio || 1, 2), 3, 2048 / w));
      renderer.setSize(w, w, false);
      camera.aspect = 1; camera.updateProjectionMatrix(); requestFrame();
    }
    function runDemo() {
      if (!motionAllowed() || disposed) return;
      demoStart = performance.now(); pointerInside = false; requestFrame();
    }
    function tick(time) {
      frame = 0;
      if (disposed || !visible || document.hidden) return;
      const dt = lastTime ? Math.min((time - lastTime) / 1000, .05) : 1 / 60; lastTime = time;
      let targetX = 0, targetY = 0, targetLift = 0;
      let targetBow = restingPaper.bow, targetCurl = restingPaper.curl, targetTwist = restingPaper.twist;
      const max = THREE.MathUtils.degToRad(configuration.tilt);
      if (motionAllowed()) {
        if (demoStart !== null) {
          const t = Math.min(1, (time - demoStart) / 2500), envelope = Math.sin(Math.PI * t) ** 2;
          targetY = Math.sin(t * Math.PI * 2) * max * envelope;
          targetX = -Math.sin(t * Math.PI) * max * .38 * envelope;
          targetLift = .006 * motionScale * envelope;
          targetBow += .030 * envelope;
          targetCurl += .010 * envelope;
          targetTwist += .023 * Math.sin(t * Math.PI * 2) * envelope;
          if (t === 1) demoStart = null;
        } else if (pointerInside || hasFocus) {
          const mouseMax = pointerInside ? max * 1.44 : max;
          targetY = px * mouseMax; targetX = py * mouseMax * .62; targetLift = pointerInside ? .0054 * motionScale : 0;
          targetBow += .035 * Math.abs(px) + .010 * Math.abs(py);
          targetCurl += .014 * py;
          targetTwist += .025 * px + .010 * py;
        }
      }
      const blend = 1 - Math.exp(-dt / configuration.inertia);
      rx += (targetX - rx) * blend; ry += (targetY - ry) * blend; lift += (targetLift - lift) * blend;
      const flexDelta = Math.abs(bow - targetBow) + Math.abs(curl - targetCurl) + Math.abs(twist - targetTwist);
      if (flexDelta > .000001) {
        bow += (targetBow - bow) * blend;
        curl += (targetCurl - curl) * blend;
        twist += (targetTwist - twist) * blend;
        updatePaper();
      }
      model.rotation.set(rx, ry, 0, 'YXZ'); model.position.y = lift;
      shadow.style.transform = `scale(${(1 - lift * 4).toFixed(4)},${(1 + lift * 4).toFixed(4)})`;
      shadow.style.opacity = (1 - lift * 13).toFixed(3);
      renderer.render(scene, camera);
      root.dataset.pose = `${(rx * 180 / Math.PI).toFixed(2)},${(ry * 180 / Math.PI).toFixed(2)}`;
      const moving = demoStart !== null || flexDelta > .00001 || Math.abs(rx - targetX) + Math.abs(ry - targetY) + Math.abs(lift - targetLift) > .00002;
      if (moving) requestFrame(); else lastTime = 0;
    }
    stage.addEventListener('pointermove', event => {
      if (event.pointerType === 'touch' || !motionAllowed()) return;
      const bounds = stage.getBoundingClientRect();
      px = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - .5) * 2));
      py = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - .5) * 2));
      pointerInside = true; demoStart = null; requestFrame();
    }, { signal: life.signal, passive: true });
    for (const event of ['pointerleave', 'pointercancel']) stage.addEventListener(event, () => {
      pointerInside = false; px = py = 0; requestFrame();
    }, { signal: life.signal });
    stage.addEventListener('focus', () => { hasFocus = true; }, { signal: life.signal });
    stage.addEventListener('blur', () => { hasFocus = false; px = py = 0; requestFrame(); }, { signal: life.signal });
    stage.addEventListener('keydown', event => {
      if (!motionAllowed()) return;
      const directions = { ArrowLeft: [-.7, 0], ArrowRight: [.7, 0], ArrowUp: [0, -.7], ArrowDown: [0, .7] };
      if (directions[event.key]) { event.preventDefault(); [px, py] = directions[event.key]; hasFocus = true; demoStart = null; requestFrame(); }
      if (event.key === 'Escape') { px = py = 0; demoStart = null; requestFrame(); }
    }, { signal: life.signal });
    reduced.addEventListener('change', controlsChanged, { signal: life.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && frame) { cancelAnimationFrame(frame); frame = 0; }
      else { lastTime = 0; requestFrame(); }
    }, { signal: life.signal });
    renderer.domElement.addEventListener('webglcontextlost', event => {
      event.preventDefault(); dispose();
      status.textContent = 'Die 3D-Ansicht wurde unterbrochen. Das Poster bleibt sichtbar.';
    }, { signal: life.signal });
    resizeObserver = new ResizeObserver(resize); resizeObserver.observe(stage);
    visibilityObserver = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (!visible && frame) { cancelAnimationFrame(frame); frame = 0; }
      if (visible) {
        lastTime = 0;
        if (coarse.matches && !playedMobile && entry.intersectionRatio >= .4 && motionAllowed()) { playedMobile = true; runDemo(); }
        else requestFrame();
      }
    }, { threshold: [0, .4] });
    visibilityObserver.observe(stage);
    resize(); controlsChanged(); renderer.render(scene, camera);
    root.dataset.ready = 'true';
    clearTimeout(loadTimer); status.textContent = '';
    announcement.textContent = 'Die interaktive 3D-Ansicht ist bereit.';
    window.addEventListener('pagehide', event => { if (!event.persisted) dispose(); }, { signal: life.signal });
    window.addEventListener('pageshow', () => { lastTime = 0; requestFrame(); }, { signal: life.signal });
  } catch (error) {
    dispose();
    console.warn('Petrol Portraits Premium Print preview:', error);
    status.textContent = 'Die 3D-Ansicht konnte hier nicht gestartet werden. Das Poster bleibt sichtbar.';
  }
}

if (!customElements.get('pp-premium-preview')) {
  customElements.define('pp-premium-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.startObserver?.disconnect();
      this.startObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.startObserver.disconnect(); initializePremium(this);
      }, { rootMargin: '400px' });
      this.startObserver.observe(this);
    }
    disconnectedCallback() { this.startObserver?.disconnect(); this.disposePreview?.(); }
  });
}
